import React, { FC, memo } from 'react';
import { css } from '@emotion/core';
import ButtonPrimaryDark from '@nexus/lib-react/dist/core/Button/ButtonPrimaryDark';
import ButtonTertiaryDark from '@nexus/lib-react/dist/core/Button/ButtonTertiaryDark';
import { useI18n } from '../../i18n';
import Loader from './Loader';

import S, { BtnDirectionsType } from './style/Modal';

interface ModalProps {
  children: React.ReactNode;
  title?: string;
  closeLabel?: string;
  confirmLabel?: string | React.ReactNode;
  onClose?: () => void;
  onConfirm?: () => void;
  position?: 'top' | 'center' | 'bottom';
  withActions?: boolean;
  customActions?: React.ReactNode | null;
  loading?: boolean;
  customWidth?: string;
  btnDirection?: BtnDirectionsType;
  // if inverseButtons is true - main button (dark) will be for confirmation, small one for cancel
  inverseButtons?: boolean;
  bottomSection?: React.ReactNode | null;
}

const backdropStyleProps = {
  css: css`
    box-sizing: border-box;
  `,
};

const Modal: FC<ModalProps> = ({
  title,
  closeLabel,
  confirmLabel,
  onClose,
  onConfirm,
  children,
  position = 'center',
  withActions = true,
  customActions = null,
  loading = false,
  customWidth = '100%',
  btnDirection = 'column',
  // if inverseButtons is true - main button (dark) will be for confirmation, small one for cancel
  inverseButtons = false,
  bottomSection = null,
}) => {
  const i18n = useI18n();
  const showActions = !customActions && withActions;
  const NotInverseButtons = showActions && (
    <S.ModalActionsBtnsWrapper btnDirection={btnDirection}>
      {onClose && (
        <ButtonPrimaryDark data-cy="modal-close" mb={10} onClick={onClose}>
          {closeLabel || i18n('dlv_generics_cancel')}
        </ButtonPrimaryDark>
      )}
      {onConfirm && (
        <ButtonTertiaryDark data-cy="modal-accept" onClick={onConfirm}>
          {confirmLabel || i18n('dlv_generics_accept')}
        </ButtonTertiaryDark>
      )}
    </S.ModalActionsBtnsWrapper>
  );
  const InverseButtons = showActions && (
    <S.ModalActionsBtnsWrapper btnDirection={btnDirection}>
      {onConfirm && (
        <ButtonPrimaryDark data-cy="modal-accept" mb={10} onClick={onConfirm}>
          {confirmLabel || i18n('dlv_generics_accept')}
        </ButtonPrimaryDark>
      )}
      {onClose && (
        <ButtonTertiaryDark data-cy="modal-close" onClick={onClose}>
          {closeLabel || i18n('dlv_generics_cancel')}
        </ButtonTertiaryDark>
      )}
    </S.ModalActionsBtnsWrapper>
  );

  return (
    <S.ModalContainer
      position={position}
      data-testid="modal"
      data-cy="modal"
      title={!loading && title}
      onClose={onClose}
      width={customWidth}
      btnDirection={btnDirection}
      backdropProps={backdropStyleProps}
      maskClosable
      closableOnEscPress
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          {children}
          <S.ModalActions>
            {!inverseButtons ? NotInverseButtons : InverseButtons}
            {customActions}
          </S.ModalActions>
          {bottomSection}
        </>
      )}
    </S.ModalContainer>
  );
};

export default memo(Modal);
